<template>
  <div
    class="field"
    :class="{ 'has-floating-label': hasFloatingLabel }"
  >
    <label
      v-if="label && !hasFloatingLabel"
      class="label"
      :for="id"
    >
      {{ label }}
      <span
        v-if="required"
        class="has-text-danger"
      >*</span>
    </label>

    <div
      class="control"
      :class="{
        'has-icons-left': leftIcon,
        'has-icons-right': rightIcon || showError
      }"
    >
      <input
        :id="id"
        :type="type"
        :value="modelValue"
        class="input"
        :class="{
          [`is-${size}`]: size,
          'is-invalid': showError,
        }"
        :placeholder="hasFloatingLabel ? '' : placeholder"
        :disabled="disabled"
        :required="required"
        :readonly="readonly"
        :min="min"
        :max="max"
        :maxlength="maxlength"
        :autocomplete="autocomplete"
        v-bind="$attrs"
        @input="updateValue"
        @blur="handleBlur"
        @focus="handleFocus"
      >

      <label
        v-if="label && hasFloatingLabel"
        class="floating-label"
        :for="id"
        :class="{ 'has-value': !!modelValue }"
      >
        {{ label }}
        <span
          v-if="required"
          class="has-text-danger"
        >*</span>
      </label>

      <span
        v-if="leftIcon"
        class="icon is-small is-left"
      >
        <i :class="leftIcon" />
      </span>

      <span
        v-if="rightIcon && !showError"
        class="icon is-small is-right"
      >
        <i :class="rightIcon" />
      </span>

      <span
        v-if="showError"
        class="icon is-small is-right has-text-danger"
      >
        <i class="fas fa-exclamation-triangle" />
      </span>
    </div>

    <p
      v-if="helpText && !showError"
      class="help"
    >
      {{ helpText }}
    </p>

    <p
      v-if="showError"
      class="help is-danger"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, ref } from 'vue';

const props = defineProps({
  id: {
    type: String,
    default: () => `input-${Math.random().toString(36).substring(2, 9)}`,
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'text',
  },
  placeholder: {
    type: String,
    default: '',
  },
  helpText: {
    type: String,
    default: '',
  },
  errorMessage: {
    type: String,
    default: '',
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: '',
    validator: (value) => ['small', 'normal', 'medium', 'large', ''].includes(value),
  },
  leftIcon: {
    type: String,
    default: '',
  },
  rightIcon: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  min: {
    type: [String, Number],
    default: undefined,
  },
  max: {
    type: [String, Number],
    default: undefined,
  },
  maxlength: {
    type: [String, Number],
    default: undefined,
  },
  autocomplete: {
    type: String,
    default: 'off',
  },
  validateOnBlur: {
    type: Boolean,
    default: false,
  },
  hasFloatingLabel: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'blur', 'focus', 'change']);

// Internal state
const isDirty = ref(false);
const isTouched = ref(false);

// Computed properties
const showError = computed(() => {
  if (props.validateOnBlur) {
    return props.hasError && isTouched.value;
  }
  return props.hasError;
});

// Methods
const updateValue = (event) => {
  const target = event.target;
  emit('update:modelValue', target.value);
  isDirty.value = true;
};

const handleBlur = (event) => {
  isTouched.value = true;
  emit('blur', event);
};

const handleFocus = (event) => {
  emit('focus', event);
};
</script>

<style scoped lang="scss">
@import '@/styles/abstracts/colors';

.field {
  position: relative;
  margin-bottom: 1.5rem;

  &.has-floating-label {
    .icon {
      top: 5px;
    }

    .control {
      position: relative;
    }

    .input {
      padding-top: 1.25rem;
      padding-bottom: 0.5rem;
      height: auto;
      min-height: 3.2rem;
    }

    .floating-label {
      position: absolute;
      left: 35px;
      top: 50%;
      transform: translateY(-50%);
      transition: all 0.2s ease;
      color: #666;
      font-size: 1rem;
      pointer-events: none;
      padding: 0 5px;
      z-index: 1;
      background: transparent;

      .body--dark & {
        color: rgba(187, 187, 187, 0.7);
      }

      &.has-value,
      input:focus ~ & {
        top: 8px;
        transform: translateY(0);
        left: 35px;
        font-size: 0.75rem;
        color: $indiefy-blue;
        font-weight: 500;

        .body--dark & {
          color: rgba(255, 255, 255, 0.7);
        }
      }
    }

    .icon.is-left ~ .floating-label {
      left: 40px;

      &.has-value,
      input:focus ~ & {
        left: 14px;
      }
    }

    // Icon styles are moved to the global styles outside of the has-floating-label selector
  }

  .label {
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .input {
    border-radius: 8px;
    transition: all 0.2s ease;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
    padding-left: 0.75rem;
    padding-right: 0.75rem;

    &:focus {
      border-color: $indiefy-blue;
      box-shadow: 0 0 0 2px rgba($indiefy-blue, 0.15);
    }

    &.is-invalid {
      border-color: #ef4444;

      &:focus {
        box-shadow: 0 0 0 2px rgba(#ef4444, 0.15);
      }
    }

    .body--dark & {
      background-color: rgba(255, 255, 255, 0.06);
      border-color: rgba(255, 255, 255, 0.1);
      color: #f1f5f9;

      &:focus {
        border-color: rgba($indiefy-blue, 0.7);
      }

      &::placeholder {
        color: rgba(255, 255, 255, 0.4);
      }
    }
  }

  .control.has-icons-left .input {
    padding-left: 2.5rem;
  }

  .control.has-icons-right .input {
    padding-right: 2.5rem;
  }

  .icon {
    color: #64748b;
    width: 2.5rem;
    height: 2.5rem;
    position: absolute;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;

    .body--dark & {
      color: rgba(255, 255, 255, 0.5);
    }

    &.has-text-danger {
      color: #ef4444 !important;
    }

    &.is-left {
      left: 0;
    }

    &.is-right {
      right: 0;
    }
  }

  .help {
    font-size: 0.8rem;
    margin-top: 0.4rem;

    &.is-danger {
      color: #ef4444;
    }
  }
}
</style>